import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '../api/client'
import { useAuthStore, selectSetUser } from '../stores/authStore'
import { useLocale } from '../i18n/context'
import type { User } from '../types'

/**
 * Landing page after a successful Stripe Checkout.
 *
 * The Pro plan is activated by the webhook (which typically arrives before the
 * browser redirect), so re-fetching /me here ensures the Zustand store reflects
 * the upgraded plan immediately.
 */
export default function BillingSuccessPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const setUser = useAuthStore(selectSetUser)
  const { tr } = useLocale()
  const bl = tr.billing

  useEffect(() => {
    // Re-fetch the current user so the Zustand store reflects the new plan.
    // The webhook typically activates Pro before the browser lands here.
    apiClient.get<User>('/api/v1/auth/me').then(({ data }) => {
      setUser(data)
      queryClient.invalidateQueries({ queryKey: ['me'] })
    }).catch(() => {
      // Ignore errors — the user will see the updated plan on next load.
    })

    // Auto-redirect to home after 4 seconds
    const timer = setTimeout(() => navigate('/home', { replace: true }), 4000)
    return () => clearTimeout(timer)
  }, [queryClient, navigate, setUser])

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-page)',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 440,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: 'rgba(39,174,96,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckCircle2 size={36} color="var(--success)" />
        </div>

        <div>
          <h1
            style={{
              margin: '0 0 10px',
              fontSize: 26,
              fontWeight: 700,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            {bl.successTitle}
          </h1>
          <p style={{ margin: 0, fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {bl.successDesc}
          </p>
        </div>

        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>
          {bl.redirectingMsg}
        </p>

        <Link
          to="/home"
          style={{
            padding: '12px 28px',
            borderRadius: 50,
            background: 'linear-gradient(135deg, #F59E0B, #D97706)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            textDecoration: 'none',
            fontFamily: 'var(--font-ui)',
          }}
        >
          {bl.goToHome}
        </Link>
      </div>
    </div>
  )
}
